<template>
  <div class="IllionFlowController">
    <svg-use
      v-if="getInstitutionLogo && flowStage !== flowStages.SELECT_INSTITUTION"
      :name="getInstitutionLogo"
      :key="getInstitutionLogo"
      class="bankImage"
    />
    <div class="logo" v-if="!getInstitutionLogo && flowStage !== flowStages.SELECT_INSTITUTION">
      <img class="logosize" :src="getLogo(selectedInstitution)" />
    </div>
    <template v-if="showUploadedStatus">
      <uploaded-status :status="status" :showStandByMessage="counter > 1" />

      <electronic-upload-error
        v-if="showErrorMessage"
        @switchToManualMode="onSwitchToManualMode"
        @tryAgain="onTryAgain"
      />
    </template>
    <template v-else>
      <bank-selection
        v-if="flowStage == flowStages.SELECT_INSTITUTION"
        :institutionLogos="institutionLogos"
        :institutions="institutions"
        @bankSelected="onBankSelected"
        ref="bankSelection"
      >
        <template #bank-selection-error-message>
          <i18n class="bankSelectionerrorMessage" path="illion.noResultFound">
            <span slot="manualUploadMessage" class="link">
              <a
                @click="$emit('switchToManualMode')"
                v-text="$t('illion.uploadError.manualUploadMessage')"
              />
            </span>
          </i18n>
        </template>
      </bank-selection>

      <profile-connection
        v-else-if="flowStage == flowStages.PROVIDE_CREDENTIALS"
        :userCredentials="userCredentials"
        :credentials="institutionCredentials"
        @userCredentialInput="onUserCredentialInput"
      >
        <slot name="illion-flow-consents" slot="illion-flow-consents" />
        <template #error-banner>
          <electronic-upload-error-banner
            v-if="showErrorBanner"
            :translationPath="errorBannerMessagePath"
            @switchToManualMode="onSwitchToManualMode"
            @tryAgain="onTryAgain"
            class="errorBanner"
          />
        </template>
      </profile-connection>

      <mfa-request-form
        v-else-if="flowStage == flowStages.MFA_REQUESTED"
        :fields="fields"
        :userFieldValues="userFieldValues"
        @userFieldsInput="onUserFieldsInput"
      >
        <template #error-banner>
          <electronic-upload-error-banner
            v-if="showErrorBanner"
            :translationPath="errorBannerMessagePath"
            @switchToManualMode="onSwitchToManualMode"
            @tryAgain="onTryAgain"
            class="errorBanner"
          />
        </template>
      </mfa-request-form>

      <account-selection
        v-else-if="flowStage == flowStages.SELECT_ACCOUNT"
        :accounts="accounts"
        :selectedAccount="selectedAccount"
        @accountSelected="onAccountSelected"
      >
        <template #error-banner>
          <electronic-upload-error-banner
            v-if="showErrorBanner"
            :translationPath="errorBannerMessagePath"
            @switchToManualMode="onSwitchToManualMode"
            @tryAgain="onTryAgain"
            class="errorBanner"
          />
          <electronic-upload-error-banner
            v-else-if="unsupportedAccount"
            :translationPath="'illion.unsupportedAccountMessage'"
            @switchToManualMode="onSwitchToManualMode"
            @tryAgain="onTryAgain"
            class="errorBanner"
          />
        </template>
      </account-selection>
      <brand-button
        v-if="flowStage !== flowStages.SELECT_INSTITUTION"
        type="button"
        class="startAgainButton"
        @click="startAgainFn"
      >
        {{ $t('startAgainButtonLabel') }}
      </brand-button>
      <brand-button
        v-if="flowStage !== flowStages.SELECT_INSTITUTION"
        type="button"
        :disabled="mfaFormInvalid"
        class="connectAccountButton"
        @click="onClick"
      >
        {{ $t('illion.connectAccountLabel') }}
      </brand-button>
    </template>
  </div>
</template>
<script>
// eslint-disable-next-line import/extensions
import BankSelection from '@/components/illionComponents/BankSelection';
import ProfileConnection from '@/components/illionComponents/ProfileConnection';
import AccountSelection from '@/components/illionComponents/AccountSelection';
import MfaRequestForm from '@/components/illionComponents/MfaRequestForm';
import ElectronicUploadError from '@/components/illionComponents/ElectronicUploadError';
import ElectronicUploadErrorBanner from '@/components/illionComponents/ElectronicUploadErrorBanner';
import UploadedStatus from '@/components/finicityComponents/UploadedStatus';
import * as statementsStatuses from '@/modules/finicity-service/statements-statuses';
import * as errorTypes from '@/modules/api-error/types';
import illionErrorCodes from '@/modules/illion-error-codes';
import {
  getProfile,
  getInstitutions,
  enrollEBS,
  resumeEnrollEBS,
  completeEnrollEBS,
  retryEnrollEBS,
  deleteAccount,
} from '@/modules/api-service/v3';
import flowStages from '@/modules/illion/stages';
import sleep from '@/utils/sleep';

export const LOGO_URL_DOMAIN = 'https://www.bankstatements.com.au/images/institutions';

const UNSUPPORTED_ACCOUNT_TYPES = [
  'credit card',
  'personal loan',
  'home loan',
  'car loan',
  'insurance',
  'superannuation',
  'investments',
];

const INACTIVE = 'inactive';

const SUPPORTED_BANK = {
  region: 'au',
  minDays: 180,
};

export default {
  name: 'IllionFlowController',
  components: {
    BankSelection,
    ProfileConnection,
    AccountSelection,
    ElectronicUploadError,
    UploadedStatus,
    MfaRequestForm,
    ElectronicUploadErrorBanner,
  },
  props: {
    application: {
      type: Object,
      required: true,
    },
    updateApplicationWithStore: {
      type: Function,
      required: true,
    },
    institutionLogos: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      mfaFormInvalid: false,
      selectedInstitution: '',
      selectedAccountId: '',
      profileConnected: false,
      accounts: [],
      showErrorMessage: false,
      status: statementsStatuses.INDETERMINATE,
      flowStage: '',
      flowStages,
      institutions: [],
      userFieldValues: {},
      fields: [],
      userCredentials: {},
      fetchStatusSleepTotal: 4 * 60 * 1000, // 4 min
      fetchStatusSleepBetweenRetries: 5 * 1000, // 5 s
      showUploadedStatus: true,
      counter: 0,
      accountStatus: '',
      errorBannerMessagePath: '',
      showErrorBanner: false,
      PRODUCT_TYPE: 'renewal-tl',
    };
  },
  async created() {
    try {
      await this.checkStatus();
    } catch (error) {
      this.handleError(error);
    }
  },
  computed: {
    getInstitutionLogo() {
      const institution =
        this.institutionLogos.find(({ slug }) => this.selectedInstitution === slug) || {};
      return institution.svgName;
    },
    fetchStatusMaxRetries() {
      // takes into account net sleep time only, doesn't account for response times
      return this.fetchStatusSleepTotal / this.fetchStatusSleepBetweenRetries;
    },
    selectedAccount() {
      if (this.accounts.length && this.selectedAccountId) {
        return this.accounts[this.selectedAccountId].key;
      }
      return '';
    },
    institutionCredentials() {
      return this.institutions.find(({ slug }) => this.selectedInstitution === slug).credentials;
    },
    unsupportedAccount() {
      const account = this.accounts.find(acc => acc.key === this.selectedAccountId);

      return account ? UNSUPPORTED_ACCOUNT_TYPES.includes(account.accountType) : false;
    },
    unsupportedBank() {
      const institution = this.institutions.find(({ slug }) => this.selectedInstitution === slug);
      return institution ? !institution.isSupported : false;
    },
    includedInstitutions() {
      const institutions = this.$env.VUE_APP_ILLION_INCLUDED_INSTITUTIONS || '';
      return institutions.split(',');
    },
  },
  methods: {
    getLogo(slug) {
      return `${LOGO_URL_DOMAIN}/${slug}.png`;
    },
    convertInstitutions(data) {
      /* eslint-disable camelcase */
      this.institutions = data.institutions.map(
        ({ slug, credentials, name, requires_mfa, region, max_days }) => {
          const institutionWithLogo =
            this.institutionLogos.find(institution => slug === institution.slug) || {};
          const { svgName } = institutionWithLogo;
          const isSupported =
            this.includedInstitutions.includes(slug) ||
            (region === SUPPORTED_BANK.region && max_days >= SUPPORTED_BANK.minDays);

          return {
            slug,
            credentials,
            name,
            svgName,
            requiresMfa: requires_mfa,
            isSupported,
          };
        },
      );
    },
    startAgainFn() {
      try {
        this.showErrorMessage = false;
        this.showErrorBanner = false;
        this.selectedInstitution = '';
        this.counter = 0;
        retryEnrollEBS(this.application.id);
        this.checkStatus();
      } catch (error) {
        this.handleError(error);
      }
    },
    async checkStatus() {
      if (this.counter > this.fetchStatusMaxRetries) {
        this.handleError();
        return;
      }
      try {
        const { data } = await getProfile(this.application.id).catch(async e => {
          const is404 = e.type === errorTypes.NOT_FOUND;

          if (is404) {
            return {};
          }
          throw e;
        });

        this.$emit('electronicStatusReceived');
        this.emitElectronicUploadInProgress(true);
        if (!data) {
          const { data: institutions } = await getInstitutions(this.application.id);

          this.convertInstitutions(institutions);
          this.flowStage = this.flowStages.SELECT_INSTITUTION;
          this.showUploadedStatus = false;
          this.emitElectronicUploadInProgress(false);
        } else if (data && data.downloadStatus) {
          const { downloadStatus } = data;
          this.accountStatus = data.status;
          if (
            downloadStatus === statementsStatuses.INCOMPLETE ||
            downloadStatus === statementsStatuses.ERROR
          ) {
            this.selectedInstitution = data.profileData.institution;
            this.handleError();
          } else if (downloadStatus === statementsStatuses.COMPLETE) {
            this.selectedInstitution = data.profileData.institution;
            this.status = statementsStatuses.COMPLETE;
            await this.removeAccount();
            this.emitElectronicUploadInProgress(false);
            this.$emit('electronicUploadSuccessful');
          } else if (downloadStatus === statementsStatuses.PENDING) {
            this.selectedInstitution = data.profileData.institution;
            this.status = statementsStatuses.PENDING;
            await sleep(this.fetchStatusSleepBetweenRetries);
            this.counter += 1;
            this.checkStatus();
          } else if (downloadStatus === statementsStatuses.PROFILE_NOT_COMPLETE) {
            if (data.status === INACTIVE) {
              this.emitElectronicUploadInProgress(false);
            }
            const { data: institutions } = await getInstitutions(this.application.id);

            this.convertInstitutions(institutions);
            this.flowStage = this.flowStages.SELECT_INSTITUTION;
            this.showUploadedStatus = false;
          }
        }
        this.cleanFields();
      } catch (error) {
        this.handleError(error);
      }
    },
    onBankSelected(institution) {
      this.selectedInstitution = institution;
      if (this.unsupportedBank) {
        this.setErrorMessage(this.$t('illion.unsupportedBankMessage'));
        return;
      }
      this.flowStage = this.flowStages.PROVIDE_CREDENTIALS;
    },
    async onClick() {
      let invalidCounter = 0;
      this.$validations.collections[0].forEach((value, key) => {
        if (key === 'overdraft-limit' && value.component.empty === true) {
          invalidCounter += 1;
        } else if (key.includes('agreement-checkbox') && value.component.checked === false) {
          invalidCounter += 1;
        }
      });
      if (
        (!this.$validations.touch() && this.application.product !== this.PRODUCT_TYPE) ||
        invalidCounter > 0
      ) {
        return;
      }
      this.showErrorBanner = false;
      this.showErrorMessage = false;
      this.accountStatus = '';

      try {
        if (this.flowStage === this.flowStages.PROVIDE_CREDENTIALS) {
          const userCredentials = { ...this.userCredentials };

          this.cleanFields();
          this.status = statementsStatuses.INDETERMINATE;
          this.showUploadedStatus = true;
          this.application.consents.forEach(consent => delete consent.links);

          await this.updateApplicationWithStore();

          this.emitElectronicUploadInProgress(true);
          const { data } = await enrollEBS(this.application.id, {
            institution: this.selectedInstitution,
            ...userCredentials,
          });
          this.accountStatus = data.status;

          if (data && this.accountStatus === 'mfa_required') {
            this.fields = data.profileData.fields;
            this.flowStage = this.flowStages.MFA_REQUESTED;
          } else if (data && data.profileData.accounts) {
            this.convertAccounts(data);
            this.flowStage = this.flowStages.SELECT_ACCOUNT;
          }
          this.showUploadedStatus = false;
        } else if (this.flowStage === this.flowStages.MFA_REQUESTED) {
          const userFieldValues = { ...this.userFieldValues };

          this.cleanFields();
          this.status = statementsStatuses.INDETERMINATE;
          this.showUploadedStatus = true;

          const { data } = await resumeEnrollEBS(this.application.id, userFieldValues);

          this.convertAccounts(data);
          this.flowStage = this.flowStages.SELECT_ACCOUNT;
          this.showUploadedStatus = false;
        } else if (this.flowStage === this.flowStages.SELECT_ACCOUNT && !this.unsupportedAccount) {
          const { selectedAccountId } = this;

          this.status = statementsStatuses.INDETERMINATE;
          this.showUploadedStatus = true;
          await completeEnrollEBS(this.application.id, selectedAccountId);
          await this.checkStatus();
        }
      } catch (error) {
        this.handleError(error);
      }
    },
    onAccountSelected(accountId) {
      this.selectedAccountId = accountId;
    },
    onUserFieldsInput(userFieldValues, hasFormInvalid) {
      this.mfaFormInvalid = !!hasFormInvalid;
      this.userFieldValues = userFieldValues;
    },
    convertAccounts(data) {
      this.accounts = data.profileData.accounts.map(account => {
        return {
          key: account.id,
          value: account.accountNumber,
          accountType: account.accountType,
        };
      });
    },
    onUserCredentialInput(value) {
      this.userCredentials = value;
    },
    emitElectronicUploadInProgress(value) {
      this.$emit('electronicUploadInProgress', value);
    },
    handleError(error) {
      this.$emit('electronicStatusReceived');
      if (
        error &&
        error.type === errorTypes.BAD_REQUEST &&
        error.data &&
        error.data.errorCode &&
        illionErrorCodes.includes(`${error.data.errorCode}`)
      ) {
        this.showUploadedStatus = false;
        this.showErrorBanner = true;
        this.errorBannerMessagePath = `illion.${error.data.errorCode}`;
      } else {
        this.showUploadedStatus = true;
        this.status = statementsStatuses.INCOMPLETE;
        this.showErrorMessage = true;
      }
      if (this.accountStatus === INACTIVE) {
        this.emitElectronicUploadInProgress(false);
      }
    },
    async onTryAgain() {
      try {
        this.status = statementsStatuses.INDETERMINATE;
        this.showUploadedStatus = true;
        this.showErrorMessage = false;
        this.showErrorBanner = false;
        this.userCredentials = {};
        this.selectedInstitution = '';
        this.counter = 0;

        await retryEnrollEBS(this.application.id);
        await this.checkStatus();
      } catch (error) {
        this.handleError(error);
      }
    },
    async onSwitchToManualMode() {
      const tempStatus = this.status;

      this.showErrorMessage = false;
      this.showErrorBanner = false;
      this.status = statementsStatuses.INDETERMINATE;
      await this.removeAccount();
      this.status = tempStatus;
      this.flowStage = this.flowStages.SELECT_INSTITUTION;
      this.showErrorMessage = true;
      this.emitElectronicUploadInProgress(false);
      this.$emit('switchToManualMode');
    },
    async removeAccount() {
      try {
        if (this.accountStatus !== INACTIVE) {
          await deleteAccount(this.application.id);
          this.accountStatus = INACTIVE;
        }
      } catch (error) {
        if (error.type === errorTypes.NOT_FOUND) {
          return;
        }
        this.$router.replace({ name: 'unableProcess' });
      }
    },
    cleanFields() {
      this.$nextTick().then(() => {
        this.userFieldValues = {};
        this.userCredentials = {};
        this.mfaFormInvalid = false;
      });
    },
    setErrorMessage(msg) {
      const { bankSelection } = this.$refs;
      if (bankSelection) {
        bankSelection.$children[0].$refs.inputText.setErrorMessage(msg);
      }
    },
  },
};
</script>
<style lang="sass" scoped>
$border-radius: 4px !default

.IllionFlowController
  background-color: $color-system-neutral-white

  .errorBanner
    margin-top: baseunit(2)

.logo
  display: flex

.bankImage
  width: 100%
  height: baseunit(8)
  margin: 0 auto

.logosize
  height: baseunit(8)
  margin: 0 auto

.connectAccountButton
  margin: $glass-spacing-5 $glass-spacing-105 $glass-spacing-1

.startAgainButton
  margin: $glass-spacing-5 0 $glass-spacing-1
  background-color: $color-white-four
  color: $glass-color-brand-primary-light-06
  border: 1px solid $glass-color-brand-primary-light-06
  box-shadow: 0 0 0 4px $glass-color-system-neutral-white
  &:hover, &.demo-hover
    color: $glass-color-brand-primary-dark-03
    background-color: mix($glass-color-brand-primary-light-06, white, 20%)
    border: 1px solid $glass-color-brand-primary-light-06

.bankSelectionErrorMessage
  display: block
  padding: $glass-spacing-1
  border-radius: $border-radius
  color: $color-system-validation-error
  +font-size(13.33, 18)
  &:hover, &.focused
    background-color: rgba($color-system-neutral-gray-5, 0.08)
    color: $color-system-validation-error

.link
  text-decoration: underline
  cursor: pointer
</style>
